import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { C, FONTS } from '../../tokens/design';
import { useScanSchedule, useScanHistory, useScanScheduleHistory } from '../../hooks/useClientData';
import { useClient } from '../../context/ClientContext';
import { useUser } from '../../context/UserContext';
import { useApiClient } from '../../api/apiClient';
import { relativeTime, exactTime } from '../../utils/time';
import Shell from '../../components/layout/Shell';
import Card from '../../components/ui/Card';
import LiveActionButton from '../../components/ui/LiveActionButton';
import EmptyState from '../../components/ui/EmptyState';
import Skeleton from '../../components/ui/Skeleton';
import PageError from '../../components/ui/PageError';
import Tag from '../../components/ui/Tag';
import { redactDemoText } from '../../utils/demoRedaction';

const FREQUENCIES = [
  { label: 'Daily (24h)', value: 24 },
  { label: 'Every 3 Days (72h)', value: 72 },
  { label: 'Weekly (168h)', value: 168 },
  { label: 'Bi-Weekly (336h)', value: 336 },
  { label: 'Monthly (720h)', value: 720 },
];

export const SCAN_SCOPES = [
  { key: 'full', label: 'Full Scan', sub: 'All enabled modules and targets', accent: C.red },
  { key: 'vulnerability', label: 'Vulnerability', sub: 'Nessus and Shodan CVE coverage', accent: C.amb },
  { key: 'osint', label: 'OSINT', sub: 'Domains, exposed services, breaches', accent: C.pur },
  { key: 'cloud', label: 'Cloud', sub: 'AWS, Azure, M365 posture', accent: C.sky },
  { key: 'm365', label: 'Microsoft 365', sub: 'Microsoft Graph baseline without Azure resource checks', accent: C.sky },
  { key: 'ad', label: 'Active Directory', sub: 'Domain, privilege, and hygiene checks', accent: C.grn },
  { key: 'dast', label: 'DAST', sub: 'Web application and route testing', accent: C.amb },
  { key: 'sensitive-data', label: 'Sensitive Data', sub: 'File shares, cloud stores, exposed data', accent: C.red },
];

const SCOPE_BY_KEY = Object.fromEntries(SCAN_SCOPES.map(scope => [scope.key, scope]));

const STATUS_LABELS = { 0: 'Queued', 1: 'Running', 2: 'Completed', 3: 'Failed' };

const FREQUENCY_VALUES = new Set(FREQUENCIES.map(f => f.value));

const toValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const firstPresent = (...values) => values.find(v => v !== undefined && v !== null && v !== '');

const formatSourceLabel = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const clientSafeScheduleText = (value) => redactDemoText(String(value ?? ''), { enabled: true })
  .replace(/runner/gi, 'scanner')
  .replace(/operator/gi, 'analyst')
  .replace(/scheduler/gi, 'automation');

export const normalizeScheduleSnapshot = (data) => {
  const candidate = Array.isArray(data)
    ? data[0]
    : data?.schedule ?? data?.item ?? data;

  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;

  const hasScheduleSignal = [
    'scheduleId', 'id', 'frequencyHours', 'cronExpression', 'nextRunAt', 'nextScanAt',
    'lastRunAt', 'lastScanAt', 'isActive', 'enabled', 'profileName', 'lastErrorMessage',
  ].some(key => candidate[key] !== undefined && candidate[key] !== null && candidate[key] !== '');

  if (!hasScheduleSignal) return null;

  const rawFrequency = Number(firstPresent(candidate.frequencyHours, candidate.intervalHours));
  const frequencyHours = Number.isFinite(rawFrequency) && rawFrequency > 0 ? rawFrequency : null;
  const nextRunAt = firstPresent(candidate.nextRunAt, candidate.nextScanAt, candidate.nextScheduledRunAt);
  const lastRunAt = firstPresent(candidate.lastRunAt, candidate.lastScanAt, candidate.lastCompletedAt);
  const activeValue = firstPresent(candidate.isActive, candidate.enabled, candidate.active);
  const retryCount = Number(firstPresent(candidate.retryCount, candidate.retries, candidate.failedAttempts, 0));
  const source = firstPresent(candidate.source, candidate.scheduleSource, candidate.origin, candidate.createdBy, candidate.updatedBy);
  const timeZone = firstPresent(candidate.timeZone, candidate.timezone, candidate.timeZoneId, candidate.tz);

  return {
    ...candidate,
    frequencyHours,
    isActive: activeValue !== false,
    nextRunAt: toValidDate(nextRunAt) ? nextRunAt : null,
    lastRunAt: toValidDate(lastRunAt) ? lastRunAt : null,
    profileName: String(candidate.profileName || candidate.scanProfile || 'full').trim().toLowerCase(),
    retryCount: Number.isFinite(retryCount) && retryCount > 0 ? retryCount : 0,
    lastErrorMessage: firstPresent(candidate.lastErrorMessage, candidate.lastError, candidate.errorMessage, ''),
    source: formatSourceLabel(source) || '',
    timeZone: typeof timeZone === 'string' && timeZone.trim() ? timeZone.trim() : '',
  };
};

export const normalizeScheduleList = (data) => {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.schedules)
      ? data.schedules
      : Array.isArray(data?.items)
        ? data.items
        : data?.schedule
          ? [data.schedule]
          : data
            ? [data]
            : [];

  return raw
    .map(item => normalizeScheduleSnapshot(item))
    .filter(Boolean)
    .sort((a, b) => {
      const ai = SCAN_SCOPES.findIndex(scope => scope.key === a.profileName);
      const bi = SCAN_SCOPES.findIndex(scope => scope.key === b.profileName);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi) || a.profileName.localeCompare(b.profileName);
    });
};

export const getScheduleStatus = ({ schedule, enabled }) => {
  if (!schedule) return { label: 'Unconfigured', color: C.t3, detail: 'No schedule exists for this client.' };
  if (schedule.isActive === false) {
    return {
      label: enabled ? 'Resume pending' : 'Paused',
      color: C.amb,
      detail: enabled ? 'Save to resume automated runs.' : 'Schedule exists, but automatic runs are paused.',
    };
  }
  if (!enabled) return { label: 'Pause pending', color: C.amb, detail: 'Save to pause automated runs.' };
  return { label: 'Active', color: C.grn, detail: 'Automatic runs are enabled.' };
};

export const getScheduleSourceLabel = (schedule) => {
  if (!schedule) return 'Not configured';
  return schedule.source || 'Configured schedule';
};

export const getScheduleTimeZoneLabel = (schedule) => {
  if (schedule?.timeZone) return schedule.timeZone;
  return 'Browser local';
};

export const getScanJobSourceLabel = (job) => {
  const source = firstPresent(job?.source, job?.trigger, job?.triggeredBy, job?.origin);
  return formatSourceLabel(source) || 'Scan job';
};

export const getSchedulerHistorySummary = (events = []) => {
  const list = Array.isArray(events) ? events : [];
  const hasStatus = (event, terms) => {
    const status = `${event?.statusLabel ?? event?.status ?? ''}`.toLowerCase();
    const type = `${event?.eventLabel ?? event?.eventType ?? ''}`.toLowerCase();
    const reason = `${event?.reasonCode ?? ''}`.toLowerCase();
    return terms.some(term => status.includes(term) || type.includes(term) || reason.includes(term));
  };

  return {
    total: list.length,
    latest: list[0] ?? null,
    lastQueued: list.find(event => hasStatus(event, ['queue', 'queued'])) ?? null,
    lastFailure: list.find(event => hasStatus(event, ['fail', 'error', 'timeout', 'unavailable'])) ?? null,
    lastSkipped: list.find(event => hasStatus(event, ['skip', 'paused', 'disabled', 'not due'])) ?? null,
    failures: list.filter(event => hasStatus(event, ['fail', 'error', 'timeout', 'unavailable'])).length,
    skipped: list.filter(event => hasStatus(event, ['skip', 'paused', 'disabled', 'not due'])).length,
  };
};

export const getScheduleValidation = ({ enabled, frequency, schedule }) => {
  const warnings = [];

  if (enabled && (!Number.isFinite(frequency) || frequency <= 0)) {
    warnings.push('Select a valid scan frequency before saving.');
  }

  if (enabled && !FREQUENCY_VALUES.has(frequency) && !schedule?.cronExpression) {
    warnings.push('The API returned a non-standard interval. Saving will preserve it only after a valid frequency is selected.');
  }

  if (enabled && schedule && !schedule.nextRunAt) {
    warnings.push('No valid next run time is available from the API.');
  }

  if (schedule?.lastErrorMessage) {
    warnings.push('The last scheduled run reported an error.');
  }

  return warnings;
};

const getScopeLabel = (profileName) => SCOPE_BY_KEY[profileName]?.label || formatSourceLabel(profileName) || 'Custom Scope';

const getScheduleNextRun = (schedule) => schedule?.nextRunAt ?? null;
const getScheduleLastRun = (schedule) => schedule?.lastRunAt ?? null;

export const formatSchedulerDetail = ([key, value]) => {
  const normalizedKey = clientSafeScheduleText(key).trim();
  const normalizedValue = clientSafeScheduleText(value).trim();
  const rawValue = String(value ?? '').trim();
  if (!normalizedKey || !normalizedValue) return '';
  if (/createdAt|updatedAt|occurredAt|startedAt|completedAt/i.test(normalizedKey)) {
    const date = toValidDate(normalizedValue);
    return date ? `${formatSourceLabel(normalizedKey)}: ${exactTime(date)}` : '';
  }
  if (/status/i.test(normalizedKey) && /^\d+$/.test(normalizedValue)) {
    return `${formatSourceLabel(normalizedKey)}: ${statusLabel(Number(normalizedValue))}`;
  }
  if (/scannerPoolId$/i.test(normalizedKey)) {
    return `Scanner Pool: ${normalizedValue}`;
  }
  if (/id$/i.test(normalizedKey) && (rawValue.length > 18 || normalizedValue === 'demo-tag' || normalizedValue === 'demo-id')) {
    return `${formatSourceLabel(normalizedKey).replace(/\s+Id$/i, '')}: reference available`;
  }
  return `${formatSourceLabel(normalizedKey)}: ${normalizedValue}`;
};

export const formatSchedulerReference = (type, value) => {
  if (!value) return '';
  const label = type === 'job' ? 'Job' : 'Schedule';
  return `${label} reference available`;
};

const statusLabel = (status) => {
  if (typeof status === 'number') return STATUS_LABELS[status] ?? 'Unknown';
  if (typeof status === 'string' && status.trim()) {
    return status.trim().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  return 'Unknown';
};

const statusColor = (status) => {
  const normalized = statusLabel(status).toLowerCase();
  if (normalized.includes('complete')) return C.grn;
  if (normalized.includes('running') || normalized.includes('progress')) return C.sky;
  if (normalized.includes('fail') || normalized.includes('error')) return C.red;
  if (normalized.includes('skip') || normalized.includes('pause') || normalized.includes('disable')) return C.amb;
  if (normalized.includes('queue') || normalized.includes('pending')) return C.amb;
  return C.t3;
};

const futureTime = (date) => {
  const d = toValidDate(date);
  if (!d) return '-';
  const mins = Math.ceil((d.getTime() - Date.now()) / 60000);
  if (mins <= 0) return 'due now';
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.ceil(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  return `in ${Math.ceil(hrs / 24)}d`;
};

const inputStyle = {
  background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6,
  padding: '8px 10px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono,
  outline: 'none', cursor: 'pointer',
};

export default function ScanSchedule() {
  const { clientId } = useClient();
  const { isPlatformAdmin, isMspUser } = useUser();
  const canWrite = isPlatformAdmin || isMspUser;
  const api = useApiClient();
  const { data: schedulePayload, loading, error, refetch } = useScanSchedule();
  const { data: scanHistory, loading: historyLoading, error: historyError, refetch: refetchHistory } = useScanHistory();
  const {
    data: schedulerHistory,
    loading: schedulerHistoryLoading,
    error: schedulerHistoryError,
    refetch: refetchSchedulerHistory,
  } = useScanScheduleHistory();
  const schedules = useMemo(() => normalizeScheduleList(schedulePayload), [schedulePayload]);
  const scheduleMap = useMemo(
    () => Object.fromEntries(schedules.map(item => [item.profileName, item])),
    [schedules],
  );
  const scans = Array.isArray(scanHistory) ? scanHistory : [];

  const [selectedScope, setSelectedScope] = useState('full');
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState(168);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const schedule = scheduleMap[selectedScope] ?? null;

  useEffect(() => {
    const selected = scheduleMap[selectedScope] ?? null;
    setEnabled(selected?.isActive ?? false);
    setFrequency(selected?.frequencyHours ?? (selectedScope === 'full' ? 168 : 24));
  }, [scheduleMap, selectedScope]);

  const handleSave = async () => {
    if (!clientId) {
      setToast({ ok: false, msg: 'Select a client before saving a scan schedule.' });
      return;
    }
    if (!Number.isFinite(frequency) || frequency <= 0) {
      setToast({ ok: false, msg: 'Select a valid scan frequency before saving.' });
      return;
    }

    setSaving(true);
    setToast(null);
    try {
      await api.put(`/clients/${clientId}/scan-schedule`, {
        isActive: enabled,
        frequencyHours: frequency,
        profileName: selectedScope,
      });
      setToast({ ok: true, msg: `${getScopeLabel(selectedScope)} schedule saved successfully.` });
      setTimeout(() => setToast(prev => prev?.ok ? null : prev), 3000);
      if (refetch) refetch();
      if (refetchHistory) refetchHistory();
      if (refetchSchedulerHistory) refetchSchedulerHistory();
    } catch (err) {
      setToast({ ok: false, msg: `Failed to save: ${err.message || 'API request failed'}` });
    } finally {
      setSaving(false);
    }
  };

  const nextRunAt = getScheduleNextRun(schedule);
  const lastRunAt = getScheduleLastRun(schedule);
  const nextScan = toValidDate(nextRunAt);
  const latestFromHistory = scans[0] ? (scans[0].completedAt ?? scans[0].endedAt ?? scans[0].createdAt) : null;
  const lastScan = toValidDate(lastRunAt) ?? toValidDate(latestFromHistory);
  const validationWarnings = getScheduleValidation({ enabled, frequency, schedule });
  const retryCount = schedule?.retryCount ?? 0;
  const recentJobs = scans.slice(0, 5);
  const recentSchedulerEvents = Array.isArray(schedulerHistory) ? schedulerHistory.slice(0, 5) : [];
  const schedulerSummary = getSchedulerHistorySummary(schedulerHistory);
  const scheduleStatus = getScheduleStatus({ schedule, enabled });
  const scheduleSource = getScheduleSourceLabel(schedule);
  const scheduleTimeZone = getScheduleTimeZoneLabel(schedule);
  const saveIntent = enabled
    ? schedule?.isActive === false
      ? 'Resume automated scan runs for this scope on the live client schedule.'
      : 'Create or update automated scan runs for this scope on the live client schedule.'
    : schedule
      ? 'Pause automated scan runs for this scope on the live client schedule.'
      : 'Create a disabled schedule record for this scope on the live client schedule.';

  return (
    <Shell title="Scan Schedule" sub="Configure recurring full scans and individual scope scans">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
        <Card style={{ padding: 22 }}>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 18, color: C.t1 }}>Scheduled Scanning</div>

          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '12px 0' }}>
              <Skeleton width="100%" height={52} />
              <Skeleton width="100%" height={40} />
              <Skeleton width={140} height={32} />
            </div>
          )}

          {error && <PageError error={error} onRetry={refetch} />}

          {!loading && !error && schedules.length === 0 && (
            <div style={{ padding: 24, background: C.s2, borderRadius: 8, border: `1px solid ${C.b1}`, textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.t1, fontFamily: FONTS.sans, marginBottom: 8 }}>Scheduled scanning is not configured</div>
              <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 14 }}>Create a recurring full scan or schedule individual scopes such as OSINT, Cloud, AD, DAST, and Sensitive Data.</div>
            </div>
          )}

          {!loading && (
            <>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Scan Scope</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 10 }}>
                  {SCAN_SCOPES.map(scope => {
                    const scopedSchedule = scheduleMap[scope.key];
                    const active = scopedSchedule?.isActive === true;
                    const selected = selectedScope === scope.key;
                    return (
                      <button
                        key={scope.key}
                        type="button"
                        onClick={() => setSelectedScope(scope.key)}
                        style={{
                          textAlign: 'left',
                          padding: '11px 12px',
                          borderRadius: 8,
                          background: selected ? `${scope.accent}12` : C.s2,
                          border: `1px solid ${selected ? scope.accent : C.b1}`,
                          borderLeft: `3px solid ${scope.accent}`,
                          cursor: 'pointer',
                          minWidth: 0,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                          <span style={{ fontSize: 11, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans }}>{scope.label}</span>
                          <Tag label={active ? 'Active' : scopedSchedule ? 'Paused' : 'Off'} color={active ? C.grn : scopedSchedule ? C.amb : C.t3} />
                        </div>
                        <div style={{ marginTop: 5, fontSize: 9, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.35 }}>{scope.sub}</div>
                        <div style={{ marginTop: 7, fontSize: 9, color: active ? C.grn : C.t3, fontFamily: FONTS.mono }}>
                          {scopedSchedule?.nextRunAt ? `Next ${futureTime(scopedSchedule.nextRunAt)}` : 'No recurring run'}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Enable/disable toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '14px 16px', background: C.s2, borderRadius: 8, border: `1px solid ${C.b1}` }}>
                <div
                  onClick={() => setEnabled(e => !e)}
                  style={{
                    width: 40, height: 20, borderRadius: 10,
                    background: enabled ? C.grn : C.t3,
                    cursor: 'pointer', position: 'relative', transition: 'background 0.15s',
                    flexShrink: 0,
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 2,
                    left: enabled ? 22 : 2, transition: 'left 0.15s',
                  }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, fontFamily: FONTS.sans }}>
                    {enabled ? `${getScopeLabel(selectedScope)} schedule enabled` : schedule ? `${getScopeLabel(selectedScope)} schedule paused` : `${getScopeLabel(selectedScope)} schedule unconfigured`}
                  </div>
                  <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>
                    {enabled ? 'This scope will run automatically at the configured cadence' : schedule ? 'Resume this saved scope schedule when it should run again' : 'Enable and save to create a recurring scope schedule'}
                  </div>
                  {schedule?.isActive === false && enabled && (
                    <div style={{ fontSize: 10, color: C.amb, fontFamily: FONTS.mono, marginTop: 4 }}>
                      Save to resume the paused schedule.
                    </div>
                  )}
                </div>
              </div>

              {/* Frequency selector */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Scan Frequency</div>
                <select
                  value={frequency}
                  onChange={e => setFrequency(Number(e.target.value))}
                  disabled={!enabled}
                  style={{ ...inputStyle, width: '100%', opacity: enabled ? 1 : 0.5 }}
                >
                  {FREQUENCIES.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>

              {/* Schedule info */}
              {enabled && (
                <div style={{ padding: '12px 14px', borderRadius: 7, background: `${C.sky}08`, border: `1px solid ${C.sky}22`, marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono, lineHeight: 1.6 }}>
                    {getScopeLabel(selectedScope)} every {(Array.isArray(FREQUENCIES) ? FREQUENCIES : []).find(f => f.value === frequency)?.label.toLowerCase() || `${frequency}h`}
                    {nextScan && <>, next scan {futureTime(nextScan)}</>}
                  </div>
                  <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.6, marginTop: 2 }}>
                    Source: {scheduleSource} | Timezone: {scheduleTimeZone}
                  </div>
                </div>
              )}

              {validationWarnings.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                  {validationWarnings.map(warning => (
                    <div key={warning} style={{
                      padding: '8px 10px', borderRadius: 6, fontSize: 10, fontFamily: FONTS.mono,
                      background: `${warning.includes('error') ? C.red : C.amb}10`,
                      border: `1px solid ${warning.includes('error') ? C.red : C.amb}33`,
                      color: warning.includes('error') ? C.red : C.amb,
                    }}>
                      {warning}
                    </div>
                  ))}
                </div>
              )}

              {toast && (
                <div style={{
                  marginBottom: 14, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono,
                  background: toast.ok ? `${C.grn}12` : C.redBg,
                  border: `1px solid ${toast.ok ? C.grn : C.red}33`,
                  color: toast.ok ? C.grn : C.red,
                }}>{toast.msg}</div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LiveActionButton
                  variant="pri"
                  onConfirm={handleSave}
                  disabled={saving || !canWrite}
                  pending={saving}
                  pendingLabel="Saving..."
                  confirmTitle={`Write ${getScopeLabel(selectedScope)} schedule`}
                  confirmDetail={`${saveIntent} This updates the selected client's scan schedule.`}
                  confirmLabel="Confirm Save"
                >
                  {saving ? 'Saving...' : canWrite ? 'Save Schedule' : 'Read Only'}
                </LiveActionButton>
              </div>
            </>
          )}
        </Card>

        {/* Schedule details sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12, color: C.t1 }}>Schedule Details</div>
            <div style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 6, background: `${scheduleStatus.color}10`, border: `1px solid ${scheduleStatus.color}33` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Operational Status</span>
                <Tag label={scheduleStatus.label} color={scheduleStatus.color} />
              </div>
              <div style={{ marginTop: 5, fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.45 }}>{scheduleStatus.detail}</div>
            </div>
            {[
              { l: 'Selected Scope', v: getScopeLabel(selectedScope) },
              { l: 'Frequency', v: (Array.isArray(FREQUENCIES) ? FREQUENCIES : []).find(f => f.value === frequency)?.label || `${frequency}h` },
              { l: 'Next Scan', v: nextScan ? exactTime(nextScan) : 'Not reported' },
              { l: 'Last Scan', v: lastScan ? relativeTime(lastScan) : 'No completed run' },
              { l: 'Timezone', v: scheduleTimeZone },
              { l: 'Source', v: scheduleSource },
              { l: 'Profile', v: schedule?.profileName || selectedScope },
              { l: 'Retries', v: retryCount > 0 ? String(retryCount) : '0' },
            ].map(r => (
              <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.b2}` }}>
                <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{r.l}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: r.l === 'Retries' && retryCount > 0 ? C.red : C.t1, fontFamily: FONTS.mono, textAlign: 'right' }}>{r.v}</span>
              </div>
            ))}
            {schedule?.lastErrorMessage && (
              <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: `${C.red}10`, border: `1px solid ${C.red}33`, color: C.red, fontSize: 10, fontFamily: FONTS.mono, lineHeight: 1.5 }}>
                Last error: {clientSafeScheduleText(schedule.lastErrorMessage)}
              </div>
            )}
          </Card>

          <Card style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1 }}>Scheduler History</div>
              <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>
                {schedulerHistoryLoading ? 'Loading...' : `${recentSchedulerEvents.length} shown`}
              </span>
            </div>
            {schedulerHistoryError && <PageError error={schedulerHistoryError} onRetry={refetchSchedulerHistory} />}
            {schedulerHistoryLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[0, 1, 2].map(i => <Skeleton key={i} width="100%" height={34} />)}
              </div>
            )}
            {!schedulerHistoryLoading && !schedulerHistoryError && recentSchedulerEvents.length === 0 && (
              <div style={{ padding: '12px 10px', borderRadius: 7, background: C.s2, border: `1px solid ${C.b1}` }}>
                <EmptyState title="No automation decisions recorded" sub="Once automated scans run, this will show submitted scans, skipped windows, and scan errors." />
              </div>
            )}
            {!schedulerHistoryLoading && !schedulerHistoryError && recentSchedulerEvents.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                {[
                  { l: 'Last Decision', v: schedulerSummary.latest?.eventLabel ?? 'Recorded', c: statusColor(schedulerSummary.latest?.statusLabel) },
                  { l: 'Last Queued', v: schedulerSummary.lastQueued?.occurredAt ? relativeTime(schedulerSummary.lastQueued.occurredAt) : 'None', c: schedulerSummary.lastQueued ? C.grn : C.t3 },
                  { l: 'Failures', v: String(schedulerSummary.failures), c: schedulerSummary.failures > 0 ? C.red : C.grn },
                  { l: 'Skipped', v: String(schedulerSummary.skipped), c: schedulerSummary.skipped > 0 ? C.amb : C.t3 },
                ].map(item => (
                  <div key={item.l} style={{ padding: '8px 9px', borderRadius: 6, background: C.s2, border: `1px solid ${C.b1}`, minWidth: 0 }}>
                    <div style={{ fontSize: 8, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{item.l}</div>
                    <div style={{ fontSize: 11, color: item.c, fontFamily: FONTS.mono, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.v}</div>
                  </div>
                ))}
              </div>
            )}
            {!schedulerHistoryLoading && !schedulerHistoryError && recentSchedulerEvents.map(event => {
              const occurred = toValidDate(event.occurredAt);
              const color = statusColor(event.status ?? event.statusLabel);
              const details = event.details && typeof event.details === 'object'
                ? Object.entries(event.details).filter(([, value]) => value !== null && value !== undefined && value !== '').slice(0, 3)
                : [];
              return (
                <div key={event.id} style={{ padding: '8px 0', borderBottom: `1px solid ${C.b2}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: C.t1, fontWeight: 700, fontFamily: FONTS.sans }}>
                        {event.eventLabel}
                      </div>
                      <div style={{ marginTop: 3, fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.45 }}>
                        {occurred ? relativeTime(occurred) : 'No timestamp reported'}
                        {' | '}
                        {clientSafeScheduleText(event.source)}
                        {event.profileName ? ` | ${event.profileName}` : ''}
                      </div>
                    </div>
                    <Tag label={event.statusLabel} color={color} />
                  </div>
                  {(event.reasonCode || event.scheduleId || event.clientName) && (
                    <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {event.reasonCode && <Tag label={formatSourceLabel(clientSafeScheduleText(event.reasonCode))} color={C.amb} />}
                      {event.clientName && <Tag label={event.clientName} color={C.sky} />}
                      {event.scheduleId && <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{formatSchedulerReference('schedule', event.scheduleId)}</span>}
                    </div>
                  )}
                  {event.explanation && (
                    <div style={{ marginTop: 5, padding: '6px 8px', borderRadius: 6, background: C.s2, color: C.t2, fontSize: 10, fontFamily: FONTS.mono, lineHeight: 1.45, wordBreak: 'break-word' }}>
                      {clientSafeScheduleText(event.explanation)}
                    </div>
                  )}
                  {details.length > 0 && (
                    <div style={{ marginTop: 5, fontSize: 9, color: C.t3, fontFamily: FONTS.mono, lineHeight: 1.45, wordBreak: 'break-word' }}>
                      {details.map(formatSchedulerDetail).filter(Boolean).join(' | ')}
                    </div>
                  )}
                  {event.scanJobId && (
                    <div style={{ marginTop: 4, fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>
                      {formatSchedulerReference('job', event.scanJobId)}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>

          <Card style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1 }}>Recent Scan Jobs</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Link to="/client/scans" style={{ fontSize: 10, color: C.sky, fontFamily: FONTS.mono, textDecoration: 'none', fontWeight: 700 }}>
                  View history
                </Link>
                <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{historyLoading ? 'Loading...' : `${recentJobs.length} shown`}</span>
              </div>
            </div>
            {historyError && <PageError error={historyError} onRetry={refetchHistory} />}
            {historyLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[0, 1, 2].map(i => <Skeleton key={i} width="100%" height={28} />)}
              </div>
            )}
            {!historyLoading && !historyError && recentJobs.length === 0 && (
              <EmptyState title="No scan jobs yet" sub="Scheduled and manual scan runs will appear here once jobs are queued." />
            )}
            {!historyLoading && !historyError && recentJobs.map(job => {
              const started = toValidDate(job.createdAt ?? job.startedAt);
              const completed = toValidDate(job.completedAt ?? job.endedAt);
              const status = statusLabel(job.status);
              const source = getScanJobSourceLabel(job);
              return (
                <div key={job.id ?? job.scanId ?? `${job.profileName}-${job.createdAt}`} style={{ padding: '8px 0', borderBottom: `1px solid ${C.b2}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <Link to="/client/scans" style={{ fontSize: 11, color: C.t1, fontWeight: 700, fontFamily: FONTS.sans, textDecoration: 'none' }}>
                      {job.profileName ?? job.scanType ?? job.label ?? 'General'}
                    </Link>
                    <Tag label={status} color={statusColor(job.status)} />
                  </div>
                  <div style={{ marginTop: 4, fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.45 }}>
                    {completed ? `Completed ${relativeTime(completed)}` : started ? `Started ${relativeTime(started)}` : 'No timestamp reported'}
                    {' | '}
                    {source}
                  </div>
                  {(job.errorMessage || job.lastErrorMessage) && (
                    <div style={{ marginTop: 4, fontSize: 10, color: C.red, fontFamily: FONTS.mono, wordBreak: 'break-word' }}>
                      {job.errorMessage || job.lastErrorMessage}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>

          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 10, color: C.t1 }}>Scan Profiles</div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.6 }}>
              Use Full Scan for broad recurring coverage, or schedule individual scopes when OSINT, vulnerability, cloud, AD, DAST, or sensitive-data checks need different cadences.
            </div>
          </Card>
        </div>
      </div>
    </Shell>
  );
}
